import { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useYieldCallback } from '@zillow/yield-callback';
import { InView } from 'react-intersection-observer';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import type { ListingDetails } from 'app/types/listingDetails.type';
import RouteActions from 'app/shared/flux/actions/RouteActions';
import TotalMonthlyPriceBadge from 'app/shared/modules/TotalMonthlyPrice/TotalMonthlyPriceBadge';
import { shouldShowTotalMonthlyPriceBadge } from 'app/shared/utils/totalMonthlyPriceUtils';
import { emitTrackHdpSimilarListingClickEvent } from 'app/shared/models/Clickstream/HdpClickstreamEvents';
import {
  emitTrackClicksIntoPropertyEvent,
  emitTrackImpressionForPropertyCardEvent,
} from 'app/shared/models/Clickstream/ListingCardClickstreamEvents';
import { ChevronLeftIcon, ChevronRightIcon } from 'app/shared/halo/components/ui/icons';
import IconCheckmark from 'images/icons/checkmark-green.svg';
import type { CellValue, ComparisonRow } from './comparisonTableUtils';
import { formatCellValue, STATIC_CATEGORIES } from './comparisonTableUtils';
import { useSimilarListingsFetch } from './useSimilarListingsFetch';
import { useScrollAffordance } from './useScrollAffordance';
import * as styles from './similarListingsModule.styles';

const ROW_LABEL_COLUMN_WIDTH = 90;
const CURRENT_LISTING_COLUMN_WIDTH = 124;
const SIMILAR_LISTING_COLUMN_WIDTH = 116;

const renderComparableValue = (value: CellValue) => {
  if (value === true) {
    return <img src={IconCheckmark} alt="Yes" className={styles.checkmarkIcon} />;
  }

  if (value === false) {
    return (
      <>
        <span className={styles.srOnly}>No</span>
        <span aria-hidden="true">{formatCellValue(null)}</span>
      </>
    );
  }

  return formatCellValue(value);
};

type ListingHeaderColumnProps = {
  row: ComparisonRow;
  isCurrent: boolean;
  onClick: (e: React.MouseEvent<HTMLAnchorElement>, listing: ListingDetails) => void;
  onImpression: (listing: ListingDetails) => void;
};

const ListingHeaderColumn = memo(function ListingHeaderColumn({
  row,
  isCurrent,
  onClick,
  onImpression,
}: ListingHeaderColumnProps) {
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => onClick(e, row.listing),
    [onClick, row.listing],
  );
  const handleInViewChange = useCallback(
    (inView: boolean) => {
      if (!inView) return;
      onImpression(row.listing);
    },
    [onImpression, row.listing],
  );

  const headerContent = (
    <>
      {row.image ? (
        <div className={styles.listingImageWrapper}>
          <img src={row.image} alt="" className={styles.listingImage} loading="lazy" />
        </div>
      ) : (
        <div className={styles.noPhotoPlaceholder}>No photo</div>
      )}
      {isCurrent ? (
        <div className={styles.currentListingText}>
          <p className={styles.currentListingLabel}>This listing</p>
          <h3 className={styles.currentListingName}>{row.title}</h3>
        </div>
      ) : (
        <h3 className={styles.listingName}>{row.title}</h3>
      )}
    </>
  );

  return (
    <th
      scope="col"
      className={`${styles.listingHeader} ${isCurrent ? styles.currentListingHeader : ''}`}
      aria-label={isCurrent ? `This listing, ${row.title}` : row.title}
    >
      {isCurrent || !row.link ? (
        <div className={`${styles.columnHeader} ${styles.columnHeaderCurrent}`}>{headerContent}</div>
      ) : (
        <InView onChange={handleInViewChange} triggerOnce>
          <a href={row.link} onClick={handleClick} className={styles.columnHeader}>
            {headerContent}
          </a>
        </InView>
      )}
    </th>
  );
});

export const SimilarListingsModule = () => {
  const dispatch = useDispatch();
  const breadcrumbs = useSelector(
    (state: RootReduxState) => state.currentListingDetails?.currentListing?.areas?.breadcrumbs,
  );
  const searchSlug = useSelector((state: RootReduxState) => state.filter.search.slug);

  const { rows, bedroomLabels, hasCurrentListingColumn } = useSimilarListingsFetch();
  const { scrollRef, canScrollLeft, canScrollRight, handleScroll } = useScrollAffordance({
    rowsLength: rows.length,
    rowLabelColumnWidth: ROW_LABEL_COLUMN_WIDTH,
  });

  const yieldNavigate = useYieldCallback((path: string) => {
    window.router.transitionTo(path);
  });

  const trackImpression = useCallback((listing: ListingDetails) => {
    emitTrackImpressionForPropertyCardEvent({
      listing,
      triggerLocation: 'similar_listings_module_halo',
    });
  }, []);

  const handleListingClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, listing: ListingDetails) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
      e.preventDefault();
      emitTrackHdpSimilarListingClickEvent({ triggerLocation: 'home_details_halo' });
      emitTrackClicksIntoPropertyEvent({
        listing,
        triggerLocation: 'similar_listings_module_halo',
      });
      if (listing.uriV2) yieldNavigate(listing.uriV2);
    },
    [yieldNavigate],
  );

  const handleScrollLeft = useCallback(() => handleScroll('left'), [handleScroll]);
  const handleScrollRight = useCallback(() => handleScroll('right'), [handleScroll]);

  const colStyles = useMemo(
    () =>
      rows.map((row, index) => ({
        width: `${
          hasCurrentListingColumn && index === 0 ? CURRENT_LISTING_COLUMN_WIDTH : SIMILAR_LISTING_COLUMN_WIDTH
        }px`,
      })),
    [rows, hasCurrentListingColumn],
  );

  const tableWidth = useMemo(
    () =>
      ROW_LABEL_COLUMN_WIDTH +
      (hasCurrentListingColumn ? CURRENT_LISTING_COLUMN_WIDTH : 0) +
      (rows.length - (hasCurrentListingColumn ? 1 : 0)) * SIMILAR_LISTING_COLUMN_WIDTH,
    [rows.length, hasCurrentListingColumn],
  );

  const mostSpecificArea = useMemo(() => breadcrumbs?.[breadcrumbs.length - 1], [breadcrumbs]);

  const areaUri = useMemo(
    () =>
      mostSpecificArea?.resourceId && searchSlug
        ? `/${mostSpecificArea.resourceId}/${searchSlug}`
        : (dispatch(RouteActions.getAreaPath()) as unknown as string),
    [dispatch, mostSpecificArea, searchSlug],
  );

  const handleAreaLinkClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
      e.preventDefault();
      yieldNavigate(areaUri);
    },
    [yieldNavigate, areaUri],
  );

  if (rows.length === 0) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.tableWrapper}>
        {canScrollLeft && (
          <button
            type="button"
            className={`${styles.arrowButton} ${styles.arrowButtonLeft}`}
            onClick={handleScrollLeft}
            aria-label="Scroll to previous similar listing"
          >
            <ChevronLeftIcon />
          </button>
        )}
        {canScrollRight && (
          <button
            type="button"
            className={`${styles.arrowButton} ${styles.arrowButtonRight}`}
            onClick={handleScrollRight}
            aria-label="Scroll to next similar listing"
          >
            <ChevronRightIcon />
          </button>
        )}

        <div ref={scrollRef} className={styles.scrollContainer}>
          <table className={styles.table} style={{ width: `${tableWidth}px` }}>
            <caption className={styles.srOnly}>Similar listings comparison</caption>
            <colgroup>
              <col style={{ width: `${ROW_LABEL_COLUMN_WIDTH}px` }} />
              {rows.map((row, columnIndex) => (
                <col key={`col-${row.listing.maloneLotIdEncoded}-${columnIndex}`} style={colStyles[columnIndex]} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th scope="col" className={styles.rowLabelHeader} />
                {rows.map((row, columnIndex) => (
                  <ListingHeaderColumn
                    key={`${row.listing.maloneLotIdEncoded}-${columnIndex}`}
                    row={row}
                    isCurrent={hasCurrentListingColumn && columnIndex === 0}
                    onClick={handleListingClick}
                    onImpression={trackImpression}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {bedroomLabels.map((label, rowIndex) => (
                <tr key={`price-row-${label}`}>
                  <th scope="row" className={`${styles.rowLabelCell} ${rowIndex === 0 ? styles.firstBodyRow : ''}`}>
                    {label}
                  </th>
                  {rows.map((row, columnIndex) => (
                    <td
                      key={`price-${label}-${row.listing.maloneLotIdEncoded}-${columnIndex}`}
                      className={`${styles.valueCell} ${styles.priceCell} ${rowIndex === 0 ? styles.firstBodyRow : ''}`}
                    >
                      {formatCellValue(row.prices[label] ?? null)}
                    </td>
                  ))}
                </tr>
              ))}

              {rows.some((row) => shouldShowTotalMonthlyPriceBadge(row.listing)) && (
                <tr>
                  <th scope="row" className={styles.rowLabelCell}>
                    <span className={styles.srOnly}>Total monthly price</span>
                  </th>
                  {rows.map((row, columnIndex) => (
                    <td
                      key={`total-price-${row.listing.maloneLotIdEncoded}-${columnIndex}`}
                      className={`${styles.valueCell} ${styles.totalPriceBadgeCell}`}
                    >
                      {shouldShowTotalMonthlyPriceBadge(row.listing) && (
                        <TotalMonthlyPriceBadge listing={row.listing} iconVariant="default" size="sm" />
                      )}
                    </td>
                  ))}
                </tr>
              )}

              {STATIC_CATEGORIES.map((category, rowIndex) => {
                const isStriped = rowIndex % 2 === 0;
                return (
                  <tr key={`amenity-row-${category}`} className={isStriped ? styles.stripedRow : undefined}>
                    <th scope="row" className={`${styles.rowLabelCell} ${isStriped ? styles.rowLabelCellStriped : ''}`}>
                      {category}
                    </th>
                    {rows.map((row, columnIndex) => (
                      <td
                        key={`amenity-${category}-${row.listing.maloneLotIdEncoded}-${columnIndex}`}
                        className={styles.valueCell}
                      >
                        {renderComparableValue(row.amenities[category])}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {mostSpecificArea?.name && (
        <div className={styles.areaLinkContainer}>
          <a href={areaUri} onClick={handleAreaLinkClick} className={styles.areaLink}>
            See more listings in {mostSpecificArea.name}
            <span aria-hidden="true">&rsaquo;</span>
          </a>
        </div>
      )}
    </div>
  );
};
